import React, { useState, useEffect } from "react";
import { getFirestore, collection, getDocs } from "firebase/firestore";
import app from "../../firebase";
import { createWorker } from "tesseract.js";

const FALLBACK_DEPARTMENTS = [
  "Nursing",
  "Business Administration",
  "Information Technology",
  "Education",
  "Accountancy",
];

// ======================================================
// PDF TEXT EXTRACTION
// ======================================================
async function extractPdfText(file) {
  const pdfjsLib = await import("pdfjs-dist");

  pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.js";

  const arrayBuffer = await file.arrayBuffer();

  const pdf = await pdfjsLib.getDocument({
    data: arrayBuffer,
  }).promise;

  let fullText = "";

  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);

    const textContent = await page.getTextContent();

    const lines = [];

    textContent.items.forEach((item) => {
      const y = Math.round(item.transform[5]);

      const existing = lines.find((l) => l.y === y);

      if (existing) {
        existing.text += " " + item.str;
      } else {
        lines.push({
          y,
          text: item.str,
        });
      }
    });

    lines.sort((a, b) => b.y - a.y);

    const pageText = lines.map((l) => l.text).join("\n");

    fullText += "\n" + pageText;
  }

  return fullText;
}

// ======================================================
// OCR FALLBACK
// ======================================================
async function ocrPdfAsImages(file) {
  const pdfjsLib = await import("pdfjs-dist");

  pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.js";

  const arrayBuffer = await file.arrayBuffer();

  const pdf = await pdfjsLib.getDocument({
    data: arrayBuffer,
  }).promise;

  const worker = await createWorker("eng");

  let fullText = "";

  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);

    const viewport = page.getViewport({
      scale: 2,
    });

    const canvas = document.createElement("canvas");

    const context = canvas.getContext("2d");

    canvas.width = viewport.width;
    canvas.height = viewport.height;

    await page.render({
      canvasContext: context,
      viewport,
    }).promise;

    const imageData = canvas.toDataURL("image/png");

    const {
      data: { text },
    } = await worker.recognize(imageData);

    fullText += "\n" + text;
  }

  await worker.terminate();

  return fullText;
}

// ======================================================
// FLEXIBLE CURRICULUM PARSER
// ======================================================
function parsePdfText(rawText, department = "", academicYear = "") {
  const courses = [];

  let currentYear = "";
  let currentSemester = "";
  let totalUnits = 0;

  // =========================================
  // CLEAN TEXT
  // =========================================
  let text = rawText
    .replace(/\r/g, "")
    .replace(/[_•▪■□]+/g, " ")
    .replace(/[ \t]+/g, " ")
    .replace(/\n{2,}/g, "\n")
    .trim();

  const lines = text
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);

  // =========================================
  // PARSE HOURS
  // =========================================
  const parseHours = (title) => {
    let lec = 0;
    let lab = 0;

    let institutional = 0;
    let hospital = 0;
    let industry = 0;
    let community = 0;
    let clinical = 0;
    let practicum = 0;

    // LEC/LAB FORMAT
    const lecLab = title.match(/\((\d+)\/(\d+)\)/);

    if (lecLab) {
      lec = parseInt(lecLab[1]);
      lab = parseInt(lecLab[2]);
    }

    // HOURS FORMAT
    const hrs = title.match(/\((\d+(?:\.\d+)?)\s*hours?\)/i);

    if (hrs) {
      const hours = parseFloat(hrs[1]);

      if (/institutional/i.test(title)) {
        institutional = hours;
      }

      if (/hospital/i.test(title)) {
        hospital = hours;
      }

      if (/industry/i.test(title)) {
        industry = hours;
      }

      if (/community/i.test(title)) {
        community = hours;
      }

      if (/clinical/i.test(title)) {
        clinical = hours;
      }

      if (/practicum|practice experience/i.test(title)) {
        practicum = hours;
      }
    }

    return {
      lec,
      lab,
      institutional,
      hospital,
      industry,
      community,
      clinical,
      practicum,
    };
  };

  // =========================================
  // MERGE MULTILINE COURSES
  // =========================================
  const mergedLines = [];

  let buffer = "";

  for (let line of lines) {
    if (/^[A-Z]{2,5}\s?\d{3}/.test(line)) {
      if (buffer) {
        mergedLines.push(buffer.trim());
      }

      buffer = line;
    } else {
      buffer += " " + line;
    }
  }

  if (buffer) {
    mergedLines.push(buffer.trim());
  }

  // =========================================
  // MAIN PARSER
  // =========================================
  for (let line of mergedLines) {
    // =====================================
    // YEAR LEVEL
    // =====================================
    if (/FIRST YEAR/i.test(line)) {
      currentYear = "First Year";
      continue;
    }

    if (/SECOND YEAR/i.test(line)) {
      currentYear = "Second Year";
      continue;
    }

    if (/THIRD YEAR/i.test(line)) {
      currentYear = "Third Year";
      continue;
    }

    if (/FOURTH YEAR/i.test(line)) {
      currentYear = "Fourth Year";
      continue;
    }

    // =====================================
    // SEMESTER
    // =====================================
    if (/FIRST SEMESTER/i.test(line)) {
      currentSemester = "First Semester";
      continue;
    }

    if (/SECOND SEMESTER/i.test(line)) {
      currentSemester = "Second Semester";
      continue;
    }

    if (/INTERSEMESTER/i.test(line)) {
      currentSemester = "Intersemester";
      continue;
    }

    // =====================================
    // TOTAL UNITS
    // =====================================
    const totalMatch = line.match(
      /Total Units\s+(\d+(?:\.\d+)?)/i
    );

    if (totalMatch) {
      totalUnits = parseFloat(totalMatch[1]);
      continue;
    }

    // =====================================
    // SKIP HEADERS
    // =====================================
    if (
      /course no|course title|units|curriculum|semester/i.test(
        line
      )
    ) {
      continue;
    }

    // =====================================
    // COURSE MATCH
    // =====================================
    const courseMatch = line.match(
      /^([A-Z]{2,5}\s?\d{3})\s+(.+?)\s+(\d+(?:\.\d+)?)$/
    );

    if (!courseMatch) continue;

    let courseNo = courseMatch[1].trim();

    let courseTitle = courseMatch[2].trim();

    let units = parseFloat(courseMatch[3]);

    // =====================================
    // HOURS
    // =====================================
    const {
      lec,
      lab,
      institutional,
      hospital,
      industry,
      community,
      clinical,
      practicum,
    } = parseHours(courseTitle);

    // =====================================
    // CLEAN TITLE
    // =====================================
    courseTitle = courseTitle
      .replace(/\(\d+\/\d+\)/g, "")
      .replace(
        /\(\d+(?:\.\d+)?\s*hours?\)/gi,
        ""
      )
      .replace(/\s+/g, " ")
      .trim();

    // Default lec/lab
    let finalLec = lec;
    let finalLab = lab;

    if (lec === 0 && lab === 0) {
      finalLec = units;
      finalLab = 0;
    }

    // Prevent duplicates
    const exists = courses.find(
      (c) => c.courseNo === courseNo
    );

    if (exists) continue;

    courses.push({
      curriculumYear: academicYear,
      department,

      yearLevel: currentYear,
      semester: currentSemester,

      courseNo,
      courseTitle,

      lecHrs: finalLec,
      labHrs: finalLab,

      institutionalHrs: institutional,
      hospitalHrs: hospital,
      industryHrs: industry,
      communityHrs: community,
      clinicalHrs: clinical,
      practicumHrs: practicum,

      units,
      totalUnits,
    });
  }

  return {
    courses,
    totalUnits,
  };
}

// ======================================================
// MAIN COMPONENT
// ======================================================
export default function RegistrarCurriculum() {
  const [departments, setDepartments] = useState(
    FALLBACK_DEPARTMENTS
  );

  const [selectedDepartment, setSelectedDepartment] =
    useState("");

  const [academicYear, setAcademicYear] = useState("");

  const [courses, setCourses] = useState([]);

  const [status, setStatus] = useState(
    "Upload curriculum PDF"
  );

  const [error, setError] = useState("");

  const [isParsing, setIsParsing] = useState(false);

  const [dragActive, setDragActive] = useState(false);

  // ======================================================
  // LOAD DEPARTMENTS
  // ======================================================
  useEffect(() => {
    async function loadDepartments() {
      try {
        const db = getFirestore(app);

        const snapshot = await getDocs(
          collection(db, "departments")
        );

        const loaded = snapshot.docs
          .map((doc) => doc.data().name || doc.id)
          .filter(Boolean);

        if (loaded.length > 0) {
          setDepartments(loaded);
        }
      } catch (err) {
        console.warn(err);
      }
    }

    loadDepartments();
  }, []);

  // ======================================================
  // HANDLE PDF UPLOAD
  // ======================================================
  const handlePdfUpload = async (file) => {
    if (!file || isParsing) return;

    if (!selectedDepartment) {
      setError("Please select department");
      return;
    }

    if (!academicYear) {
      setError("Please enter academic year");
      return;
    }

    setIsParsing(true);

    setError("");

    setCourses([]);

    try {
      setStatus("Extracting PDF text...");

      let extractedText = await extractPdfText(file);

      // OCR fallback
      if (!extractedText || extractedText.length < 100) {
        setStatus("Running OCR...");
        extractedText = await ocrPdfAsImages(file);
      }

      setStatus("Parsing curriculum...");

      const result = parsePdfText(
        extractedText,
        selectedDepartment,
        academicYear
      );

      if (result.courses.length === 0) {
        setError("No courses found");

        setStatus("Parsing failed");
      } else {
        setCourses(result.courses);

        setStatus(
          `Extracted ${result.courses.length} courses`
        );
      }
    } catch (err) {
      console.error(err);

      setError("Failed to parse PDF");

      setStatus("Error");
    } finally {
      setIsParsing(false);
    }
  };

  // ======================================================
  // FILE DROP
  // ======================================================
  const handleDrop = (e) => {
    e.preventDefault();

    setDragActive(false);

    const file = e.dataTransfer.files?.[0];

    if (file) {
      handlePdfUpload(file);
    }
  };

  // ======================================================
  // FILE INPUT
  // ======================================================
  const handleFileInput = (e) => {
    const file = e.target.files?.[0];

    if (file) {
      handlePdfUpload(file);
    }
  };

  // ======================================================
  // TOTAL UNITS
  // ======================================================
  const totalUnits =
    courses.length > 0
      ? courses[0].totalUnits ||
        courses.reduce(
          (sum, c) =>
            sum + parseFloat(c.units || 0),
          0
        )
      : 0;

  return (
    <div
      style={{
        background: "#fff",
        padding: 32,
        borderRadius: 16,
        boxShadow:
          "0 8px 24px rgba(0,0,0,0.08)",
      }}
    >
      <h1
        style={{
          marginBottom: 12,
          color: "#155c2b",
        }}
      >
        Registrar Curriculum Upload
      </h1>

      <p
        style={{
          marginBottom: 24,
          color: "#666",
        }}
      >
        Upload a curriculum PDF to generate
        automatic curriculum tables
      </p>

      {/* FORM */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 16,
          marginBottom: 24,
        }}
      >
        <select
          value={selectedDepartment}
          onChange={(e) =>
            setSelectedDepartment(
              e.target.value
            )
          }
          style={inputStyle}
        >
          <option value="">
            Select Department
          </option>

          {departments.map((dept) => (
            <option key={dept} value={dept}>
              {dept}
            </option>
          ))}
        </select>

        <input
          type="text"
          placeholder="2024-2025"
          value={academicYear}
          onChange={(e) =>
            setAcademicYear(e.target.value)
          }
          style={inputStyle}
        />
      </div>

      {/* DROP AREA */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={() =>
          setDragActive(false)
        }
        onDrop={handleDrop}
        style={{
          border: `3px dashed ${
            dragActive ? "#155c2b" : "#ccc"
          }`,
          borderRadius: 12,
          padding: 40,
          textAlign: "center",
          background: dragActive
            ? "#f0fff4"
            : "#fafafa",
          marginBottom: 24,
        }}
      >
        <input
          type="file"
          accept=".pdf"
          id="pdfUpload"
          style={{ display: "none" }}
          onChange={handleFileInput}
        />

        <button
          onClick={() =>
            document
              .getElementById("pdfUpload")
              .click()
          }
          disabled={isParsing}
          style={buttonStyle}
        >
          {isParsing
            ? "Scanning PDF..."
            : "Upload PDF"}
        </button>
      </div>

      {/* STATUS */}
      {status && (
        <div style={statusStyle}>
          {status}
        </div>
      )}

      {/* ERROR */}
      {error && (
        <div style={errorStyle}>{error}</div>
      )}

      {/* TABLE */}
      {courses.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
            }}
          >
            <thead>
              <tr style={{ background: "#f5f5f5" }}>
                <th style={th}>Year</th>
                <th style={th}>Semester</th>
                <th style={th}>Course No</th>
                <th style={th}>Course Title</th>
                <th style={th}>Lec</th>
                <th style={th}>Lab</th>
                <th style={th}>
                  Institutional
                </th>
                <th style={th}>Hospital</th>
                <th style={th}>Industry</th>
                <th style={th}>Community</th>
                <th style={th}>Clinical</th>
                <th style={th}>Practicum</th>
                <th style={th}>Units</th>
              </tr>
            </thead>

            <tbody>
              {courses.map((course, index) => (
                <tr
                  key={index}
                  style={{
                    background:
                      index % 2 === 0
                        ? "#fff"
                        : "#fafafa",
                  }}
                >
                  <td style={td}>
                    {course.yearLevel}
                  </td>

                  <td style={td}>
                    {course.semester}
                  </td>

                  <td style={td}>
                    {course.courseNo}
                  </td>

                  <td style={td}>
                    {course.courseTitle}
                  </td>

                  <td style={tdCenter}>
                    {course.lecHrs}
                  </td>

                  <td style={tdCenter}>
                    {course.labHrs}
                  </td>

                  <td style={tdCenter}>
                    {course.institutionalHrs ||
                      "-"}
                  </td>

                  <td style={tdCenter}>
                    {course.hospitalHrs || "-"}
                  </td>

                  <td style={tdCenter}>
                    {course.industryHrs || "-"}
                  </td>

                  <td style={tdCenter}>
                    {course.communityHrs ||
                      "-"}
                  </td>

                  <td style={tdCenter}>
                    {course.clinicalHrs || "-"}
                  </td>

                  <td style={tdCenter}>
                    {course.practicumHrs ||
                      "-"}
                  </td>

                  <td style={tdCenter}>
                    {course.units}
                  </td>
                </tr>
              ))}
            </tbody>

            <tfoot>
              <tr
                style={{
                  background: "#f5f5f5",
                  fontWeight: 700,
                }}
              >
                <td
                  colSpan={12}
                  style={{
                    padding: 12,
                    textAlign: "right",
                  }}
                >
                  Total Units
                </td>

                <td style={tdCenter}>
                  {totalUnits}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

// ======================================================
// STYLES
// ======================================================
const inputStyle = {
  padding: 12,
  borderRadius: 8,
  border: "1px solid #ddd",
};

const buttonStyle = {
  padding: "12px 20px",
  border: "none",
  borderRadius: 8,
  background: "#155c2b",
  color: "#fff",
  fontWeight: 700,
  cursor: "pointer",
};

const statusStyle = {
  padding: 12,
  background: "#f0fff4",
  color: "#166534",
  borderRadius: 8,
  marginBottom: 16,
};

const errorStyle = {
  padding: 12,
  background: "#fee2e2",
  color: "#991b1b",
  borderRadius: 8,
  marginBottom: 16,
};

const th = {
  padding: 12,
  textAlign: "left",
  borderBottom: "2px solid #ddd",
};

const td = {
  padding: 12,
  borderBottom: "1px solid #eee",
};

const tdCenter = {
  padding: 12,
  textAlign: "center",
  borderBottom: "1px solid #eee",
};